import { tx as translateInterfaceText } from '@/utils/i18n';
/**
 * Subscription Service - Serviço para gerenciamento de assinaturas
 *
 * Fornece métodos para criar, atualizar, buscar e cancelar assinaturas
 */

import { authApi } from './authService';
import { isSurplusAttempt, type SurplusAttempt } from '@/utils/surplusPricing';

export interface CheckoutSelection {
  product_id: string;
  currency: 'brl';
  cupom?: string;
  id_Stripe_Card?: string;
  action?: 'activate_trial' | 'renew_now';
  expected_amount_cents?: number;
}
export interface CheckoutPayload extends CheckoutSelection { request_id: string }
export interface CheckoutResult {
  success: boolean;
  message?: string;
  code?: string;
  retryable?: boolean;
  definitive?: boolean;
  resumedDifferentSelection?: boolean;
  data?: { id_Subscription?: string; status?: string };
}
const pendingKey = (customer: string) => `hostvsl:subscription-checkout:${customer}`;
const unknownResult: CheckoutResult = {
  success: false, retryable: true,
  message: 'Pagamento aguardando confirmação. Consulte a mesma tentativa para confirmar o resultado.',
};
const invalidPendingMessage = 'Não foi possível ler o pagamento pendente. Entre em contato com o suporte antes de iniciar outra cobrança.';
function isCheckoutPayload(value: unknown): value is CheckoutPayload {
  const payload = value as CheckoutPayload | null;
  return typeof payload?.request_id === 'string' && !!payload.request_id &&
    typeof payload.product_id === 'string' && !!payload.product_id && payload.currency === 'brl';
}
function getPendingCheckout(customer: string): CheckoutPayload | null {
  const raw = sessionStorage.getItem(pendingKey(customer));
  if (!raw) return null;
  let pending: unknown;
  try { pending = JSON.parse(raw); }
  catch { throw new Error(invalidPendingMessage); }
  if (!isCheckoutPayload(pending)) throw new Error(invalidPendingMessage);
  return pending;
}
export { isSurplusAttempt };
export type { SurplusAttempt };
export interface PendingCheckoutResult {
  success: boolean;
  message?: string;
  code?: string;
  retryable?: boolean;
  data?: { pending: CheckoutPayload | null; kind: 'subscription' | 'surplus' | null; surplus?: SurplusAttempt };
}
async function recoverPendingCheckout(customer: string): Promise<PendingCheckoutResult> {
  try {
    const response = await authApi.get(`/subscription/checkout/${customer}/pending`);
    const body = response.data as PendingCheckoutResult;
    if (body?.success !== true || !body.data ||
        !['subscription', 'surplus', null].includes(body.data.kind) ||
        (body.data.pending !== null && !isCheckoutPayload(body.data.pending)) ||
        (body.data.kind === 'subscription' && !body.data.pending)) {
      return { success: false, retryable: true, message: body?.message || 'Não foi possível consultar os pagamentos pendentes. Tente novamente antes de pagar.' };
    }
    // A operação durável no servidor prevalece sobre qualquer sessão antiga deste navegador.
    if (body.data.pending) {
      sessionStorage.setItem(pendingKey(customer), JSON.stringify(body.data.pending));
      return body;
    }
    if (body.data.kind === 'surplus') {
      if (!isSurplusAttempt(body.data.surplus)) return { success: false, retryable: true, message: 'Não foi possível recuperar a compra de plays extras pendente. Consulte o suporte antes de iniciar outra cobrança.' };
      sessionStorage.setItem(`hostvsl:surplus-purchase:${customer}`, JSON.stringify(body.data.surplus));
      return body;
    }
    const localPending = getPendingCheckout(customer);
    return { success: true, data: { pending: localPending, kind: localPending ? 'subscription' : null } };
  } catch (error) {
    const response = (error as {response?: {data?: PendingCheckoutResult}})?.response?.data;
    return { success: false, retryable: true, message: response?.message || 'Não foi possível consultar os pagamentos pendentes. Tente novamente antes de pagar.' };
  }
}
function selectionMatches(a: CheckoutSelection, b: CheckoutSelection) {
  return a.product_id === b.product_id && a.currency === b.currency &&
    (a.cupom || '') === (b.cupom || '') && (a.id_Stripe_Card || '') === (b.id_Stripe_Card || '') && a.action === b.action && a.expected_amount_cents === b.expected_amount_cents;
}
export const subscriptionService = {
  getPendingCheckout,
  recoverPendingCheckout,
  checkout: async (customer: string, selection: CheckoutSelection): Promise<CheckoutResult> => {
    const recovered = await recoverPendingCheckout(customer);
    if (recovered.success !== true || !recovered.data) {
      return { success: false, retryable: true, code: recovered.code, message: recovered.message };
    }
    if (recovered.data.kind === 'surplus') {
      return { success: false, retryable: true, code: 'SURPLUS_PENDING', message: 'Há uma compra de plays extras aguardando confirmação. Consulte essa compra antes de iniciar outra cobrança.' };
    }
    let payload: CheckoutPayload;
    let resumedDifferentSelection = false;
    try {
      const pending = recovered.data.pending;
      resumedDifferentSelection = !!pending && !selectionMatches(pending, selection);
      payload = pending || { ...selection, request_id: crypto.randomUUID() };
      // Persistir o corpo inteiro antes de qualquer operação que possa cobrar.
      sessionStorage.setItem(pendingKey(customer), JSON.stringify(payload));
    } catch (error) {
      return { success: false, retryable: true, message: error instanceof Error ? error.message : 'Não foi possível salvar a tentativa de pagamento neste navegador.' };
    }
    let result: CheckoutResult;
    let status: number | undefined;
    try {
      const response = await authApi.post(`/subscription/checkout/${customer}`, payload);
      result = typeof response.data?.success === 'boolean' ? response.data : { ...unknownResult };
    } catch (error) {
      const response = (error as { response?: { status?: number; data?: CheckoutResult } }).response;
      status = response?.status;
      result = response?.data?.success === false ? response.data : { ...unknownResult };
    }
    const definitive = result.definitive === true ||
      (result.retryable !== true && (result.retryable === false || [400, 402, 403].includes(status || 0)));
    if (result.success === true || definitive) sessionStorage.removeItem(pendingKey(customer));
    return { ...result, resumedDifferentSelection };
  },

  /**
   * GET /subscription/:id_Stripe_Client
   * Busca assinaturas do cliente
   */
  findSub: async (idStripeClient: string) => {
    try {
      const response = await authApi.get(`/subscription/${idStripeClient}`);
      return response.data;
    } catch (error) {
      return (error as { response?: { data?: CheckoutResult } })?.response?.data || { success: false, message: 'Erro ao buscar assinaturas' };
    }
  },

  /**
   * DELETE /subscription/:id_Stripe_Subscription
   * Cancela uma assinatura
   */
  cancelSub: async (idStripeSubscription: string) => {
    try {
      const response = await authApi.delete(`/subscription/${idStripeSubscription}`);
      return response.data;
    } catch (error) {
      return (error as { response?: { data?: CheckoutResult } })?.response?.data || { success: false, message: 'Erro ao cancelar assinatura' };
    }
  },
};

export default subscriptionService;
